import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { Divider, Skeleton } from '@mantine/core';
import { api } from '~/trpc-extra/api';
import { ConditionalBlock } from './ConditionalBlock';
import { useAtomValue } from 'jotai';
import { conditionalBlockResultsAtom } from '~/atoms/recorder-atoms';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export function ConditionalOpInner({
  conditionalBlockId,
  testContentId,
  step,
  readonly,
  parentRootOperationId,
  parentOperationOrder,
  parentContexts: incomingParentContexts,
}: {
  conditionalBlockId: string;
  testContentId: string;
  step: SelectedStep;
  readonly?: boolean;
  parentRootOperationId: string;
  parentOperationOrder: number;
  parentContexts?: OperationParentContext[];
}) {
  const { data: fullConditionalBlock, isLoading: isLoadingFullConditionalBlock } =
    api.operation.getFullConditionalBlock.useQuery({ conditionalBlockId }, { enabled: !!conditionalBlockId });

  // show the current path resolution for the conditional block from the recorder
  const resolvedTo = useAtomValue(conditionalBlockResultsAtom)?.[conditionalBlockId];

  // Create parent contexts for each conditional block
  const createConditionalParentContexts = (blockType: 'then' | 'else') => {
    const conditionalContext: OperationParentContext = {
      rootOperationId: incomingParentContexts?.at(0)?.rootOperationId ?? parentRootOperationId,
      order: parentOperationOrder,
      componentId: undefined,
      conditional: {
        conditionalBlockId,
        blockType,
      },
    };
    return [...(incomingParentContexts || []), conditionalContext] satisfies OperationParentContext[];
  };

  return (
    isLoadingFullConditionalBlock ?
      <div className="flex flex-col gap-1">
        <Skeleton
          height={20}
          width="100%"
        />
        <Skeleton
          height={20}
          width="20%"
        />
        <Skeleton
          height={20}
          width="40%"
        />
      </div>
    : fullConditionalBlock ?
      <div className="flex flex-col">
        {/* If Block */}
        <ConditionalBlock
          operations={fullConditionalBlock.thenOperations || []}
          conditional={{
            conditionalBlockId: fullConditionalBlock.id,
            blockType: 'then',
          }}
          testContentId={testContentId}
          step={step}
          readonly={readonly}
          resolvedToThisBlock={resolvedTo === undefined ? undefined : resolvedTo}
          parentContexts={createConditionalParentContexts('then')}
        />

        <Divider />

        {/* Else Block */}
        <ConditionalBlock
          operations={fullConditionalBlock.elseOperations || []}
          conditional={{
            conditionalBlockId: fullConditionalBlock.id,
            blockType: 'else',
          }}
          testContentId={testContentId}
          step={step}
          readonly={readonly}
          resolvedToThisBlock={resolvedTo === undefined ? undefined : !resolvedTo}
          parentContexts={createConditionalParentContexts('else')}
        />
      </div>
    : <div className="text-sm text-gray-500">No conditional block found</div>
  );
}
